<template>
    <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
        <!-- Header -->
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-mb-6 tw-gap-4">
            <div>
                <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Company Team</h1>
                <p class="tw-text-gray-600">Meet our talented team members</p>
            </div>
            <div class="tw-flex tw-items-center tw-gap-4">
                <!-- Search Input -->
                <div class="tw-w-full sm:tw-w-80">
                    <FormInput
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search team members..."
                        left-icon="solar:magnifer-linear"
                        class="tw-w-full"
                    />
                </div>
                <!-- Member Count -->
                <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-whitespace-nowrap">
                    <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-primary-500" />
                    <span v-if="!loading">{{ filteredTeamMembers.length }} members</span>
                    <span v-else>Loading...</span>
                </div>
            </div>
        </div>

        <!-- Loading State - Team Member Cards Skeleton -->
        <div v-if="loading" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6">
            <div 
                v-for="n in 8" 
                :key="n"
                class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-6 tw-animate-pulse"
            >
                <!-- Avatar Skeleton -->
                <div class="tw-flex tw-justify-center tw-mb-4">
                    <div class="tw-w-20 tw-h-20 tw-bg-gray-200 tw-rounded-full"></div>
                </div>
                
                <!-- Name and Role Skeleton -->
                <div class="tw-text-center tw-mb-4">
                    <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-mx-auto tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-1/2 tw-mx-auto"></div>
                </div>
                
                <!-- Stats Skeleton -->
                <div v-if="false" class="tw-grid tw-grid-cols-2 tw-gap-3">
                    <div v-for="s in 4" :key="s" class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                        <div>
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-8 tw-mb-1"></div>
                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Actual Team Member Cards -->
        <div v-else class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6">
            <div 
                v-for="member in filteredTeamMembers" 
                :key="member.id"
                class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-6 hover:tw-shadow-lg hover:tw-border-primary-300 tw-transition-all tw-duration-200 tw-cursor-pointer"
                @click="viewMemberProfile(member)"
            >
                <!-- Profile Picture -->
                <div class="tw-flex tw-justify-center tw-mb-4">
                    <div class="tw-relative">
                        <img
                            v-if="member.avatar"
                            :src="member.avatar"
                            :alt="member.name"
                            class="tw-w-20 tw-h-20 tw-rounded-full tw-object-cover tw-border-3 tw-border-gray-100"
                        />
                        <div
                            v-else
                            class="tw-w-20 tw-h-20 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-3 tw-border-gray-100"
                        >
                            <icon icon="solar:user-bold-duotone" class="tw-text-3xl tw-text-primary-500" />
                        </div>
                        
                        <!-- Online Status Indicator -->
                        <div
                            v-if="member.isOnline"
                            class="tw-absolute tw-bottom-1 tw-right-1 tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-border-white tw-bg-green-500"
                        ></div>
                    </div>
                </div>

                <!-- Member Info -->
                <div class="tw-text-center tw-mb-4">
                    <h3 class="tw-font-semibold tw-text-gray-900 tw-mb-1 tw-text-lg">
                        {{ member.name }}
                    </h3>
                    <p class="tw-text-sm tw-text-gray-600 tw-font-medium">
                        {{ member.companyRole }}
                    </p>
                </div>

                <!-- Stats Grid -->
                <div v-if="false" class="tw-grid tw-grid-cols-2 tw-gap-3">
                    <!-- Projects -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:folder-with-files-bold-duotone" class="tw-text-xl tw-text-blue-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.projects }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Projects</div>
                        </div>
                    </div>

                    <!-- Meetings -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:calendar-bold-duotone" class="tw-text-xl tw-text-green-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.meetings }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Meetings</div>
                        </div>
                    </div>

                    <!-- Messages -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:chat-round-bold-duotone" class="tw-text-xl tw-text-purple-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.messages }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Messages</div>
                        </div>
                    </div>

                    <!-- Tasks -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:checklist-bold-duotone" class="tw-text-xl tw-text-orange-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.tasks }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Tasks</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State (when no loading and no members) -->
        <div v-if="!loading && filteredTeamMembers.length === 0" class="tw-text-center tw-py-12">
            <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
                {{ searchQuery ? 'No members found matching your search' : 'No team members found' }}
            </h3>
            <p class="tw-text-gray-500">
                {{ searchQuery ? 'Try adjusting your search terms or clear the search to see all members.' : 'Add team members to see them here.' }}
            </p>
            <button 
                v-if="searchQuery" 
                @click="searchQuery = ''"
                class="tw-mt-4 tw-px-4 tw-py-2 tw-bg-primary-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-primary-700 tw-transition-colors"
            >
                Clear Search
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import useAlert from '@/composables/useAlert'
import FormInput from '@/components/form/FormInput.vue'

// Loading state
const loading = ref(true)

// Search functionality
const searchQuery = ref('')

// Demo team members data
const teamMembers = ref([])

// Computed property for filtered team members
const filteredTeamMembers = computed(() => {
    if (!searchQuery.value) {
        return teamMembers.value
    }
    
    const query = searchQuery.value.toLowerCase().trim()
    return teamMembers.value.filter(member => 
        member.name.toLowerCase().includes(query) ||
        member.companyRole.toLowerCase().includes(query)
    )
})

// Demo data
const demoTeamMembers = [
    {
        id: 1,
        name: 'Sarah Johnson',
        companyRole: 'Marketing Director',
        avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=3b82f6&color=fff',
        isOnline: true,
        stats: {
            projects: 12,
            meetings: 34,
            messages: 156,
            tasks: 28
        }
    },
    {
        id: 2,
        name: 'Michael Chen',
        companyRole: 'Senior Developer',
        avatar: 'https://ui-avatars.com/api/?name=Michael+Chen&background=10b981&color=fff',
        isOnline: false,
        stats: {
            projects: 8,
            meetings: 22,
            messages: 89,
            tasks: 45
        }
    },
    {
        id: 3,
        name: 'Emily Rodriguez',
        companyRole: 'UI/UX Designer',
        avatar: 'https://ui-avatars.com/api/?name=Emily+Rodriguez&background=f59e0b&color=fff',
        isOnline: true,
        stats: {
            projects: 15,
            meetings: 28,
            messages: 203,
            tasks: 32
        }
    },
    {
        id: 4,
        name: 'David Park',
        companyRole: 'Project Manager',
        avatar: 'https://ui-avatars.com/api/?name=David+Park&background=8b5cf6&color=fff',
        isOnline: false,
        stats: {
            projects: 20,
            meetings: 67,
            messages: 124,
            tasks: 18
        }
    },
    {
        id: 5,
        name: 'Lisa Thompson',
        companyRole: 'Sales Manager',
        avatar: null, // Will show fallback icon
        isOnline: true,
        stats: {
            projects: 9,
            meetings: 41,
            messages: 178,
            tasks: 23
        }
    },
    {
        id: 6,
        name: 'Ahmed Hassan',
        companyRole: 'DevOps Engineer',
        avatar: 'https://ui-avatars.com/api/?name=Ahmed+Hassan&background=ef4444&color=fff',
        isOnline: false,
        stats: {
            projects: 7,
            meetings: 19,
            messages: 67,
            tasks: 31
        }
    },
    {
        id: 7,
        name: 'Maria Garcia',
        companyRole: 'Content Strategist',
        avatar: 'https://ui-avatars.com/api/?name=Maria+Garcia&background=06b6d4&color=fff',
        isOnline: true,
        stats: {
            projects: 11,
            meetings: 25,
            messages: 142,
            tasks: 27
        }
    },
    {
        id: 8,
        name: 'James Wilson',
        companyRole: 'Business Analyst',
        avatar: null, // Will show fallback icon
        isOnline: false,
        stats: {
            projects: 13,
            meetings: 38,
            messages: 95,
            tasks: 21
        }
    },
    {
        id: 9,
        name: 'Sophie Laurent',
        companyRole: 'Quality Assurance',
        avatar: 'https://ui-avatars.com/api/?name=Sophie+Laurent&background=84cc16&color=fff',
        isOnline: true,
        stats: {
            projects: 6,
            meetings: 15,
            messages: 73,
            tasks: 39
        }
    },
    {
        id: 10,
        name: 'Carlos Rodriguez',
        companyRole: 'System Administrator',
        avatar: 'https://ui-avatars.com/api/?name=Carlos+Rodriguez&background=f97316&color=fff',
        isOnline: false,
        stats: {
            projects: 4,
            meetings: 12,
            messages: 58,
            tasks: 16
        }
    }
]

// Methods
const viewMemberProfile = (member) => {
    useAlert(`Viewing ${member.name}'s profile`, 'info')
    // Future: Navigate to member profile page
    console.log('View member profile:', member)
}

// Load team members with simulated delay
const loadTeamMembers = async () => {
    try {
        loading.value = true
        
        // Simulate API call with 2 second delay
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        // Set demo data
        teamMembers.value = demoTeamMembers
        
    } catch (error) {
        console.error('Error loading team members:', error)
        useAlert('Failed to load team members', 'error')
    } finally {
        loading.value = false
    }
}

// Initialize component
onMounted(() => {
    loadTeamMembers()
})
</script>

<style scoped>
/* Custom hover effects for cards */
.tw-cursor-pointer:hover {
    transform: translateY(-2px);
}
</style>